import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useListSearchParams } from '@/hooks/useListSearchParams'
import { useUsers } from '../api/admin-api'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { ChevronLeft, ChevronRight, Search } from 'lucide-react'

export function UserList() {
  const navigate = useNavigate()
  const { page, filters, setPage, setFilter } = useListSearchParams({ search: '' })
  const [searchInput, setSearchInput] = useState(filters.search)
  const pageSize = 20

  const { data, isLoading, error } = useUsers({
    page,
    pageSize,
    search: filters.search || undefined,
    sortBy: 'created_at',
    sortOrder: 'desc',
  })

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setFilter('search', searchInput)
  }

  if (isLoading) return <div className="text-center py-12">로딩 중...</div>
  if (error) return <div className="text-center py-12 text-red-500">에러: {error.message}</div>

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-heading tracking-tight">사용자</h1>
          <p className="text-caption mt-2 leading-relaxed">사용자 목록 및 관리자 권한 관리</p>
        </div>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="flex gap-2 max-w-md">
        <Input
          placeholder="이메일 검색..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <Button type="submit" variant="secondary">
          <Search className="w-4 h-4" />
        </Button>
      </form>

      <div className="bg-white rounded-lg border min-w-[800px]">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                이메일
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                소스
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                기업/워크스페이스
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                가입일
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                사용량
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                관리자
              </th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {data?.items.map((user) => (
              <tr
                key={user.id}
                className="hover:bg-gray-50 cursor-pointer"
                onClick={() => navigate(`/admin/users/${user.id}/view`)}
              >
                <td className="px-6 py-4">
                  <div className="font-medium text-gray-900">{user.email}</div>
                </td>
                <td className="px-6 py-4">
                  <Badge variant="outline">{user.source}</Badge>
                </td>
                <td className="px-6 py-4 text-sm text-gray-700 whitespace-nowrap">
                  ENT {user.enterpriseId ?? '—'} / WS {user.workspaceId ?? '—'}
                </td>
                <td className="px-6 py-4 text-sm text-gray-700">
                  {new Date(user.createdAt).toLocaleDateString('ko-KR', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </td>
                <td className="px-6 py-4">
                  <Badge variant={user.hasUsage ? 'success' : 'secondary'}>
                    {user.hasUsage ? '있음' : '없음'}
                  </Badge>
                </td>
                <td className="px-6 py-4">
                  {user.isAdmin && <Badge variant="default">Admin</Badge>}
                </td>
              </tr>
            ))}
            {data?.items.length === 0 && (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                  사용자가 없습니다
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {data && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-500">
            총 {data.pagination.total}명 중 {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, data.pagination.total)}명
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm">
              {page} / {data.pagination.totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(Math.min(data.pagination.totalPages, page + 1))}
              disabled={page === data.pagination.totalPages}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
